import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesOrderItemWithVoucher,
} from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { dinero, toDecimal } from 'dinero.js';

import { VoucherInfo } from '../types/activity-column-types';

export function formatQuantity(item: BackofficeActivitiesOrderItem): number {
  const rawQuantity =
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
      ? Number(item.quantity_free_goods)
      : item.quantity !== undefined
      ? Number(item.quantity)
      : 0;

  const sanitizedQuantity = Number.isFinite(rawQuantity)
    ? Math.trunc(rawQuantity)
    : 0;

  return Number(
    toDecimal(
      dinero({
        amount: sanitizedQuantity,
        currency: STOCK_UI,
      })
    )
  );
}

export function calculateVoucherDiscount(
  price: number,
  voucherInfo: VoucherInfo
): { finalPrice: number; originalPrice: number; discountAmount?: number } {
  const originalPrice = price;
  let finalPrice = price;

  if (!voucherInfo.hasVoucherDiscount) {
    return { finalPrice, originalPrice };
  }

  if (voucherInfo.voucherDiscountPercentage) {
    const voucherDiscount =
      (finalPrice * voucherInfo.voucherDiscountPercentage) / 100;
    finalPrice = finalPrice - voucherDiscount;

    return {
      finalPrice,
      originalPrice,
      discountAmount: voucherInfo.voucherDiscountPercentage * 100,
    };
  }

  if (voucherInfo.voucherDiscountAmount) {
    finalPrice = finalPrice - voucherInfo.voucherDiscountAmount;

    return {
      finalPrice,
      originalPrice,
    };
  }

  return { finalPrice, originalPrice };
}

export type OrderItemUnitPriceFields = Pick<
  BackofficeActivitiesOrderItemWithVoucher,
  | 'price'
  | 'latest_price'
  | 'is_discount'
  | 'price_after_discount'
  | 'quantity_free_goods'
  | 'has_voucher_discount'
  | 'voucher_discount_percentage'
  | 'voucher_discount_amount'
>;

export function getPromoAdjustedUnitPrice(
  item: Pick<
    OrderItemUnitPriceFields,
    'price' | 'is_discount' | 'price_after_discount'
  >
): number {
  const catalogPrice = item.price ?? 0;

  if (item.is_discount) {
    return item.price_after_discount ?? catalogPrice;
  }

  return catalogPrice;
}

export function getVoucherAdjustedUnitPrice(
  promoAdjustedPrice: number,
  item: Pick<
    OrderItemUnitPriceFields,
    | 'has_voucher_discount'
    | 'voucher_discount_percentage'
    | 'voucher_discount_amount'
  >,
  isVoucherEnabled = false
): ReturnType<typeof calculateVoucherDiscount> {
  if (!isVoucherEnabled || !item.has_voucher_discount) {
    return {
      finalPrice: promoAdjustedPrice,
      originalPrice: promoAdjustedPrice,
    };
  }

  return calculateVoucherDiscount(promoAdjustedPrice, {
    hasVoucherDiscount: true,
    voucherDiscountPercentage: item.voucher_discount_percentage,
    voucherDiscountAmount: item.voucher_discount_amount,
  });
}

/** Unit price shown in Harga Checkout — promo first, then voucher when enabled. */
export function getOrderItemCheckoutUnitPrice(
  item: OrderItemUnitPriceFields,
  isVoucherEnabled = false
): number {
  const promoAdjustedPrice = getPromoAdjustedUnitPrice(item);

  return getVoucherAdjustedUnitPrice(promoAdjustedPrice, item, isVoucherEnabled)
    .finalPrice;
}

/**
 * Unit price shown in Harga Akhir.
 * Promo/voucher lines reuse checkout unit price; plain rows keep API latest_price.
 */
export function getOrderItemDisplayLatestPrice(
  item: OrderItemUnitPriceFields,
  isVoucherEnabled = false
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  const usesCheckoutPricing =
    Boolean(item.is_discount) ||
    (isVoucherEnabled && Boolean(item.has_voucher_discount));

  if (usesCheckoutPricing) {
    return getOrderItemCheckoutUnitPrice(item, isVoucherEnabled);
  }

  return item.latest_price ?? item.price ?? 0;
}

export function formatCurrency(amount: number): string {
  return buyerCurrencyFormatter(amount);
}

export interface ActivityImageObject {
  image?: string;
  thumbnail_url?: string;
  medium_image_url?: string;
  large_image_url?: string;
}

export function getItemCode(item: BackofficeActivitiesOrderItem): string {
  const convertToString = (
    value: string | number | undefined | null
  ): string => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  };

  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    const freeGoodsCode = convertToString(item.free_goods_product_item_code);
    if (freeGoodsCode) {
      return freeGoodsCode;
    }

    const productCode = convertToString(item.product_item_code);
    if (productCode) {
      return productCode;
    }

    const itemCode =
      'item_code' in item && item.item_code !== undefined
        ? convertToString(item.item_code)
        : '';
    if (itemCode) {
      return itemCode;
    }

    return '';
  }

  const productCode = convertToString(item.product_item_code);
  if (productCode) {
    return productCode;
  }

  const itemCode =
    'item_code' in item && item.item_code !== undefined
      ? convertToString(item.item_code)
      : '';
  if (itemCode) {
    return itemCode;
  }

  return '';
}

export function getImageUrl(item: BackofficeActivitiesOrderItem): string {
  const placeholder = 'assets/images/product_placeholder.png';

  const firstNonEmpty = (arr: unknown): string | undefined => {
    if (!Array.isArray(arr)) return undefined;
    return arr.find((url: string) => url);
  };

  // Handle array-based image fields (order items from API)
  const fromArrays =
    firstNonEmpty(item.images_medium) ||
    firstNonEmpty(item.images) ||
    firstNonEmpty(item.images_large) ||
    firstNonEmpty(item.image_thumbnails);

  if (fromArrays) return fromArrays;

  // Handle object-based image fields (promo/voucher free good items)
  const images = item.images;
  if (images && typeof images === 'object' && !Array.isArray(images)) {
    const { medium_image_url, image, large_image_url, thumbnail_url } =
      images as ActivityImageObject;
    return (
      medium_image_url ||
      image ||
      large_image_url ||
      thumbnail_url ||
      placeholder
    );
  }

  return placeholder;
}

export function isNewProduct(item: BackofficeActivitiesOrderItem): boolean {
  return Boolean(item.is_new_product);
}

export function hasFreeGoods(item: BackofficeActivitiesOrderItem): boolean {
  return Boolean(
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
  );
}

export type OrderItemSubtotalFields = {
  received_subtotal?: number | null;
  delivered_subtotal?: number | null;
  processed_subtotal?: number | null;
  subtotal?: number | null;
  saved_subtotal?: number | null;
  quantity_free_goods?: number | null;
  has_voucher_discount?: boolean;
};

export function hasSubtotalValue(
  value: number | null | undefined
): value is number {
  return value != null && value !== 0;
}

export function isOrderItemSubtotalEmpty(
  item: OrderItemSubtotalFields
): boolean {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return false;
  }

  return (
    !hasSubtotalValue(item.received_subtotal) &&
    !hasSubtotalValue(item.delivered_subtotal) &&
    !hasSubtotalValue(item.processed_subtotal) &&
    !hasSubtotalValue(item.subtotal)
  );
}

export function resolveOrderItemBaseSubtotal(
  item: Pick<
    OrderItemSubtotalFields,
    | 'received_subtotal'
    | 'delivered_subtotal'
    | 'processed_subtotal'
    | 'subtotal'
  >
): number {
  if (hasSubtotalValue(item.received_subtotal)) {
    return item.received_subtotal;
  }
  if (hasSubtotalValue(item.delivered_subtotal)) {
    return item.delivered_subtotal;
  }
  if (hasSubtotalValue(item.processed_subtotal)) {
    return item.processed_subtotal;
  }
  return item.subtotal ?? 0;
}

export function hasFulfillmentSubtotal(
  item: Pick<
    OrderItemSubtotalFields,
    'received_subtotal' | 'delivered_subtotal' | 'processed_subtotal'
  >
): boolean {
  return (
    hasSubtotalValue(item.received_subtotal) ||
    hasSubtotalValue(item.delivered_subtotal) ||
    hasSubtotalValue(item.processed_subtotal)
  );
}

export function getOrderItemDisplaySubtotal(
  item: OrderItemSubtotalFields,
  isVoucherEnabled = false
): number {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return 0;
  }

  const baseSubtotal = resolveOrderItemBaseSubtotal(item);

  if (
    isVoucherEnabled &&
    item.has_voucher_discount &&
    hasFulfillmentSubtotal(item)
  ) {
    return baseSubtotal - (item.saved_subtotal ?? 0);
  }

  return baseSubtotal;
}

export function formatVoucherDiscount(discount: {
  voucher_discount_percentage?: number;
  voucher_discount_amount?: number;
}): string {
  if (discount.voucher_discount_percentage) {
    return `Disc. ${discount.voucher_discount_percentage}%`;
  }

  if (discount.voucher_discount_amount) {
    return `Disc. Rp ${formatCurrency(discount.voucher_discount_amount)}`;
  }

  return '';
}

export function formatProductRewardImages(
  images: {
    image?: string;
    thumbnail_url?: string;
    medium_image_url?: string;
    large_image_url?: string;
  },
  defaultPlaceholder = 'assets/images/product_placeholder.png'
): {
  image: string;
  thumbnail_url: string;
  medium_image_url: string;
  large_image_url: string;
} {
  return {
    image: images.image || defaultPlaceholder,
    thumbnail_url: images.thumbnail_url || defaultPlaceholder,
    medium_image_url: images.medium_image_url || defaultPlaceholder,
    large_image_url: images.large_image_url || defaultPlaceholder,
  };
}

export function parseVoucherRuleProduct(ruleProduct: {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
}): {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
} {
  return {
    is_rule_product: ruleProduct.is_rule_product,
    voucher_codes: ruleProduct.voucher_codes || [],
    rules: ruleProduct.rules || [],
  };
}
